import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { AutoStoriesRounded } from "@mui/icons-material";
import PageHeader from "../components/page-header";
import { Dialogs } from "../components/dialogs";
import StickyHeadTable, { Column } from "../components/table";
import { Livro, LivroType } from "../types/general";
import tasks from "../utils/tasks";
import { ViewModelLivro } from "../viewmodel/viewmodel-livro";

function LivrosPage() {

  const viewModel = ViewModelLivro();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState<"all" | "suggestions">("all");

  // Form state
  const [title, setTitle] = useState("");
  const [shared, setShared] = useState("");
  const [pdfFile, setPdfFile] = useState<any>(null);
  const [type, setType] = useState<LivroType>("BOOK");
  const [author, setAuthor] = useState("");
  const [livrariaId, setLivrariaId] = useState<number>(0);

  const handleOpen = () => {
    setEditMode(false);
    setTitle("");
    setShared("");
    setPdfFile(null);
    setType("BOOK");
    setAuthor("");
    if (viewModel.livrariaIdParam) {
      setLivrariaId(Number(viewModel.livrariaIdParam));
    }
    setOpen(true);
  };

  const handleEdit = (id: number) => {
    const livro = viewModel.livros.data.find((p) => p.id === id);
    if (livro) {
      setEditMode(true);
      setTitle(livro.title);
      setShared(livro.shared || "");
      setPdfFile(livro.url);
      setType(livro.type || "BOOK");
      setAuthor(livro.author || "");
      setLivrariaId(livro.livraria_id || 0);
      viewModel.selectLivro(livro);
      setOpen(true);
    }
  };

  const handleFoto = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setPdfFile(file);
    }
  };

  const validateForm = (): string | null => {

    if (!livrariaId) {
      return "Selecione uma livraria";
    }
    if (!title.trim()) {
      return "O título é obrigatório";
    }
    if (!editMode && !pdfFile) {
      return "Por favor, selecione um ficheiro PDF.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      viewModel.showWarning({
        title: "Erro",
        message: error,
      });
      return;
    }

    setOpen(false);
    const livroData = {
      title,
      shared,
      type,
      author,
      livraria_id: livrariaId,
      url: pdfFile,
      visible: editMode && viewModel.selectedLivro?.visible,
    };

    editMode && viewModel.selectedLivro?.id
      ? viewModel.handleActionLivro(
        { id: viewModel.selectedLivro.id, ...livroData },
        "update"
      )
      : viewModel.handleActionLivro(livroData, "add");
  };

  const handleDelete = (id: number) => {
    const livro = viewModel.livros.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Eliminação",
      message: `Tem a certeza que deseja eliminar o livro "${livro?.title}"? Esta ação não pode ser desfeita.`,
      onConfirm: () => viewModel.handleDeleteLivro(id),
    });
  };

  const handleValidate = (id: number) => {
    const livro = viewModel.livros.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Validação",
      message: `Tem a certeza que deseja validar o livro "${livro?.title}"?`,
      onConfirm: () => viewModel.handleValidateLivro(id),
    });
  };

  const handleInvalidate = (id: number) => {
    const livro = viewModel.livros.data.find((p) => p.id === id);
    viewModel.showConfirmation({
      title: "Confirmar Invalidação",
      message: `Tem a certeza que deseja invalidar o livro "${livro?.title}"?`,
      onConfirm: () => viewModel.handleRejectLivro(id),
    });
  };

  const getTypeLabel = (type?: LivroType) => {
    switch (type) {
      case "BOOK":
        return "Livro";
      case "AUX":
        return "Auxiliar";
      default:
        return "Desconhecido";
    }
  };

  const getTypeColor = (type?: LivroType): "success" | "warning" | "error" | "info" => {
    switch (type) {
      case "BOOK":
        return "success";
      case "AUX":
        return "info";
      default:
        return "info";
    }
  };

  const columns: Column<Livro>[] = [
    { id: "id", label: "ID", minWidth: 80 },
    { id: "title", label: "Título", minWidth: 400 },
    {
      id: "author",
      label: "Autor",
      minWidth: 200,
    },
    {
      id: "type",
      label: "Tipo",
      minWidth: 100,
      renderCell: (row) => (
        <Chip
          label={getTypeLabel(row.type)}
          color={getTypeColor(row.type)}
          size="small"
        />
      ),
    },
    {
      id: "livraria_name",
      label: "Livraria",
      minWidth: 150,
    },
    {
      id: "visible",
      label: "Visível",
      minWidth: 80,
      renderCell: (row) => (
        <Chip
          label={row.visible ? "Sim" : "Não"}
          color={row.visible ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      minWidth: 150,
      renderCell: (row) => (
        <Box>
          <Tooltip title="Editar" arrow>
            <IconButton
              size="small"
              onClick={() => handleEdit(row.id!)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.visible ? (
            <Tooltip title="Invalidar" arrow>
              <IconButton
                size="small"
                color="warning"
                onClick={() => handleInvalidate(row.id!)}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Validar" arrow>
              <IconButton
                size="small"
                color="success"
                onClick={() => handleValidate(row.id!)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row.id!)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const data = useMemo(() => {
    return viewModel.livros.data.map((item) => {
      return {
        ...item,
        author: item.author || "Sem Info.",
        livraria_name: viewModel.livrarias.data.find((livraria) => item.livraria_id === livraria.id)?.name || "Sem Info."
      }
    })
  }, [viewModel.livrarias, viewModel.livros]);

  console.log(data)

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        icon={<AutoStoriesRounded />}
        color="#0EA5E9"
        title="Gestão de Livros"
        subtitle={viewModel.livrariaIdParam ? `Filtrado por livraria ID: ${viewModel.livrariaIdParam}` : undefined}
        onBack={viewModel.livrariaIdParam ? viewModel.goToLivrarias : () => viewModel.navigate("/painel")}
        actions={
          <>
            <Button
              variant={viewMode === "all" ? "contained" : "outlined"}
              onClick={() => {
                setViewMode("all");
                if (viewModel.livrariaIdParam) {
                  viewModel.fetchLivrosByLivraria(Number(viewModel.livrariaIdParam));
                } else {
                  viewModel.fetchLivros();
                }
              }}
            >
              Todas
            </Button>
            <Button
              variant={viewMode === "suggestions" ? "contained" : "outlined"}
              startIcon={<LightbulbIcon />}
              onClick={() => {
                setViewMode("suggestions");
                viewModel.fetchSuggestions();
              }}
            >
              Sugestões
            </Button>
            <Button variant="contained" onClick={handleOpen}>
              Novo Livro
            </Button>
          </>
        }
      />

      <StickyHeadTable
        columns={columns}
        rows={data}
        getRowId={(row) => row.id!}
        pagination={{
          totalData: viewModel.livros.pagination.totalData,
          totalPages: viewModel.livros.pagination.totalPages,
          currentPage: viewModel.livros.pagination.currentPage,
          onPageChange: (newPage) => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            if (viewModel.livrariaIdParam) {
              const livrariaIdNum = Number(viewModel.livrariaIdParam);
              viewModel.fetchLivrosByLivraria(livrariaIdNum, newPage);
            } else {
              viewModel.fetchLivros(newPage);
            }
          },
          onRowsPerPageChange: () => { },
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? "Editar Livro" : "Novo Livro"}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Livraria</InputLabel>
            <Select
              value={livrariaId}
              label="Livraria"
              onChange={(e) => setLivrariaId(Number(e.target.value))}
              disabled={!editMode && !!viewModel.livrariaIdParam}
            >
              {viewModel.livrarias.data.map((livraria) => (
                <MenuItem key={livraria.id} value={livraria.id}>
                  {livraria.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={type}
              label="Tipo"
              onChange={(e) => setType(e.target.value as LivroType)}
            >
              <MenuItem value="BOOK">Livro</MenuItem>
              <MenuItem value="AUX">Auxiliar</MenuItem>
            </Select>
          </FormControl>

          <TextField
            autoFocus
            margin="normal"
            label="Título"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <TextField
            margin="normal"
            label="Autor"
            fullWidth
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
          />

          <TextField
            margin="normal"
            label="Partilhado por"
            fullWidth
            value={shared}
            onChange={(e) => setShared(e.target.value)}
          />
          {
            editMode ?
              (<Box sx={{ mt: 2 }}>
                <a href={tasks.generateFileUrl(pdfFile)} download style={{ textDecoration: 'none' }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    fullWidth
                  >
                    Baixar Livro
                  </Button>
                </a>
              </Box>) :
              (<Box sx={{ mt: 2 }}>
                <Input
                  id="pdf-file-input"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFoto}
                />
                <label htmlFor="pdf-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    fullWidth
                  >
                    {pdfFile ? pdfFile.name : editMode ? "Alterar PDF (Opcional)" : "Selecionar PDF"}
                  </Button>
                </label>
                {pdfFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                    ✓ Ficheiro selecionado: {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                )}
              </Box>)
          }

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? "Actualizar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialogs {...viewModel.dialog} />
    </Container>
  );
}

export default LivrosPage;
